import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, forwardRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup, UntypedFormControl, Validators, ValidatorFn, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MessageService } from 'base';
import { ConfigurationService } from '../../services/configuration.service';
import { OnDestroy } from '@angular/core';
import { ComponentBase } from 'common';
import { StringUtils } from 'DiscoveryEngine.TS/Utils';
import { Integration, IntegrationNames, IntegrationTable } from '../../../DiscoveryEngine.TS/Core/Integration';
import { DefaultIntegrationSettingsChild } from '../default-integration-settings-child-interface/default-integration-settings-child-interface';


@Component({
	selector: 'itglue-default-settings',
	templateUrl: './itglue-default-settings.component.html',
	styleUrls: ['./itglue-default-settings.component.scss']
})
export class ITGlueDefaultSettingsComponent extends ComponentBase implements DefaultIntegrationSettingsChild, OnInit, OnDestroy, OnChanges {
	@Input()
	public inputSettings: Integration;

	public form: UntypedFormGroup;

	public enabled: UntypedFormControl;

	public serverURL: UntypedFormControl;
	public apiServerURL: UntypedFormControl;
	public apiKey: UntypedFormControl;

	private apiKeyChanged: boolean;
	private apiKeyChangedSub: Subscription;

	constructor(private fb: UntypedFormBuilder,
		private configService: ConfigurationService,
		private messageService: MessageService) {

		super();
	}

	public ngOnInit(): void {
		this.buildForm();
	}

	public ngOnDestroy(): void {
		if (this.apiKeyChangedSub) {
			this.apiKeyChangedSub.unsubscribe();
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.buildForm();
	}

	private buildForm(): void {
		this.enabled = undefined;
		this.serverURL = undefined;
		this.apiServerURL = undefined;
		this.apiKey = undefined;

		let enabled = true;
		let serverURL = "";
		let apiServerURL = "";
		let apiKey = "";

		// Load in the settings from the input settings variable, and set them to the form controls.
		if (this.inputSettings) {
			enabled = this.inputSettings.Enabled;
			serverURL = this.inputSettings.ServerURL;
			apiServerURL = this.inputSettings.ApiServerURL;
			apiKey = (this.inputSettings.HasApiKey ? this.hidePassword(this.inputSettings.ApiKey) : "");
		}

		this.enabled = new UntypedFormControl(enabled);
		this.serverURL = new UntypedFormControl(serverURL);
		this.apiServerURL = new UntypedFormControl(apiServerURL);
		this.apiKey = new UntypedFormControl(apiKey);

		this.apiKeyChanged = false;
		this.apiKeyChangedSub = this.apiKey.valueChanges.subscribe(() => {
			this.apiKeyChanged = true;
		});

		this.form = this.fb.group({
			enabled: this.enabled,
			serverURL: this.serverURL,
			apiServerURL: this.apiServerURL,
			apiKey: this.apiKey,
		},
		{
			validator: Validators.compose([
				this.noneOrAllSettings(),
			])
		});
	}

	public enableSave(): boolean {
		return true;
	}

	public onSave(): void {
		const outputIntegrationTable = new IntegrationTable();
		const integrationSettings = this.getIntegrationSettings();
		outputIntegrationTable.Add(integrationSettings);
		this.configService.setIntegrationTable(outputIntegrationTable).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully saved ITGlue default settings");
				this.form.markAsPristine();
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to save ITGlue default settings");
			});
	}

	public get enableTestITGlueButton(): boolean {
		return (!!this.apiServerURL && !!this.apiServerURL.value
			&& !!this.apiKey && !!this.apiKey.value);
	}

	public testConnection(): void {
		let apiKey = "";
		if (this.apiKeyChanged) {
			apiKey = StringUtils.ObfuscateString2(this.apiKey.value);
		}
		else {
			apiKey = this.inputSettings.ApiKey;
		}
		this.configService.testITGlueConnection(this.apiServerURL.value, apiKey).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully connected to ITGlue");
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to connect to ITGlue");
			});
	}

	public onPasswordKeyDownHandler(event: any): void {
		if (!this.apiKeyChanged) {
			this.apiKeyChanged = true;

			this.apiKey.setValue('');
			this.apiKey.markAsDirty();
		}
	}

	public getIntegrationSettings(): Integration {
		const outputIntegration = new Integration(IntegrationNames.ITGlue);
		outputIntegration.Enabled = this.enabled.value;
		outputIntegration.ServerURL = this.serverURL.value;
		outputIntegration.ApiServerURL = this.apiServerURL.value;
		if (this.apiKeyChanged) {
			outputIntegration.ApiKey = StringUtils.ObfuscateString2(this.apiKey.value);
		}
		else {
			outputIntegration.ApiKey = this.inputSettings.ApiKey;
		}

		return outputIntegration;
	}

	private noneOrAllSettings(): ValidatorFn {
		return () => {
			let b1 = (!!this.serverURL.value !== !!this.apiServerURL.value);
			let b2 = (!!this.apiServerURL.value !== !!this.apiKey.value);
			if (b1 || b2) {
				return {
					noneOrAllSettings: { valid: false }
				};
			}

			return null;
		}
	}

}
